import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import BreadcrumbsSection from "~/components/Breadcrumbs/BreadcrumbsSection";
import ContentWrapper from "~/components/ContentWrapper";
import SearchInput from "~/components/formElements/SearchInput/SearchInput";
import PageHeading from "~/components/headings/PageHeading.tsx";
import SectionHeading from "~/components/headings/SectionHeading";
import SectionHeadingTabs from "~/components/headings/SectionHeadingTabs";
import NoEntriesFound from "~/components/NoEntriesFound";
import { appRoutes, CENTRAL_INVOICE_RECIPIENTS_INDEX_PATH } from "~/constants/appRoutes.ts";
import type {
	CentralInvoiceRecipient,
} from "~/modules/billing/api/centralInvoiceRecipient/centralInvoiceRecipientTypes.ts";
import type { Client } from "~/modules/client/api/client/clientTypes.ts";
import type { Company } from "~/modules/client/api/company/companyTypes.ts";
import CentralInvoiceRecipientContactCard
	from "~/modules/client/components/ClientDetailsView/components/ClientsInvoiceRecipientsList/components/CentralInvoiceRecipientContactCard.tsx";
import CompaniesListHead from "~/modules/client/components/CompaniesListHead";
import type { AvatarImage } from "~/modules/image/api/avatarImage/avatarImageTypes.ts";
import { isMobileOrTablet } from "~/utils/mobileAndTabletDetection.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

interface Props {
	allCompanyAvatarImages?: AvatarImage[];
	companies?: Company[];
	centralInvoiceRecipients: CentralInvoiceRecipient[];
	clients?: Client[];
}

const CentralInvoiceRecipientDetailsView = ({
	allCompanyAvatarImages,
	companies,
	centralInvoiceRecipients,
	clients,
}: Props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [search, setSearch] = useState("");

	const searchInputRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		if (searchInputRef.current && !isMobileOrTablet()) {
			searchInputRef.current.focus();
		}
	}, []);

	const filteredRecipients = useMemo(() => {
		if (centralInvoiceRecipients) {
			return centralInvoiceRecipients.filter((recipient) => {
				return recipient.displayName.toLowerCase().includes(search.trim().toLowerCase());
			});
		}
		return [];
	}, [search, centralInvoiceRecipients]);

	return (
		<>
			<BreadcrumbsSection pages={[{ displayName: "Zentrale Rechnungsempfänger", path: "" }]}
								className="bg-white" />
			<PageHeading title={t("contacts.title", "Alle Zentralen Rechnungsempfänger")} />

			<div className="sticky top-0 z-10 border-b bg-white py-4">
				<ContentWrapper>
					<div className="w-48">
						<SearchInput
							placeholder="Name..."
							value={search}
							onChange={setSearch}
							ref={searchInputRef} />
					</div>
				</ContentWrapper>
			</div>

			<ContentWrapper>
				<SectionHeading sticky
								style={{ top: 73 }}>
					<div className="flex items-center justify-between">
						<SectionHeadingTabs
							tabs={[
								{ name: "Kunden", value: "clients" },
								{ name: "Kontakte", value: "contacts" },
								{
									name: "Zentrale Rechnungsempfänger",
									value: "central-invoice-recipients",
									active: true,
								},
							]}
							onTabClick={(tab) => {
								"clients" === tab && navigate(appRoutes.clients().path, { replace: true });
								"contacts" === tab && navigate(appRoutes.contacts().path, { replace: true });
								"central-invoice-recipients" === tab && navigate(CENTRAL_INVOICE_RECIPIENTS_INDEX_PATH, { replace: true });
							}}
						/>
					</div>
				</SectionHeading>

				<div className="mt-8 flex flex-col gap-10">
					{0 === filteredRecipients.length ? (
						<NoEntriesFound />
					) : (
						companies &&
						clients &&
						companies
							.sort(byObjectProperty("displayName"))
							.map(({ id: companyId, displayName: companyDisplayName, avatarImageId }) => {
								const companyClients = clients.filter((clients) => clients.companyId === companyId);
								const clientIds = companyClients.map((client) => client.id);
								const companiesRecipients = filteredRecipients.filter((recipient) =>
									clientIds.includes(recipient.clientId),
								);
								if (companiesRecipients.length === 0) return null;
								const avatarImage = allCompanyAvatarImages?.find((image) => image.id === avatarImageId);
								return (
									<div key={companyId}>
										<CompaniesListHead avatarImage={avatarImage}
														   companyName={companyDisplayName} />

										<div className="my-4 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
											{companiesRecipients
												.sort((a, b) => a.displayName.localeCompare(b.displayName))
												.map(recipientData => {
														const clientDetails = clients.find(
															(client) => recipientData.clientId === client.id,
														);
														const clientDisplayName =
															undefined === clientDetails
																? ""
																: clientDetails.displayName;
														return (
															<CentralInvoiceRecipientContactCard
																key={recipientData.id}
																invoiceRecipient={recipientData}
																clientDisplayName={clientDisplayName}
															/>
														);
													},
												)}
											{/*			{0 === search.length && (
												<ButtonNewItem
													size="sm"
													onClick={() => setNewContactCompanyId(companyId)}
												/>
											)}*/}
										</div>
									</div>
								);
							})
					)}
				</div>
			</ContentWrapper>
		</>
	);
};

export default CentralInvoiceRecipientDetailsView;
